/**
 * Reveal on scroll — the footer's entrance behaviour, lifted out of it and
 * shared (sections/footer.liquid keeps its own copy inline; this is the same
 * logic, same guard rails, for the homepage sections).
 *
 * Opt in from a section with:
 *
 *   <section class="x" data-reveal>            <- the observed element
 *     …                                        <- its CSS does the animating
 *   </section>
 *
 * The script only ever toggles two classes, and never touches a style:
 *
 *   .is-anim  is added once, by the script itself. Everything hidden in the
 *             CSS must sit behind it — so with JavaScript off, or under
 *             prefers-reduced-motion, the class never arrives and the section
 *             simply shows, rather than staying invisible for good.
 *   .is-in    is added when the section enters the viewport, and removed once
 *             it has fully left, so coming back down replays the entrance.
 *
 * Optional:
 *   data-reveal-threshold="0.35" — the share of the element that must be on
 *     screen to play. Observe the thing that actually animates, not the
 *     section around it: a 700px section is 12% on screen long before its
 *     title is, which is what made the entrances look like they had already
 *     happened by the time the reader arrived.
 *   data-reveal-margin="0px 0px -14% 0px" — shrinks the viewport the observer
 *     measures against. The negative bottom means the element has to clear the
 *     lowest 14% of the screen before it counts as arrived, so the entrance
 *     plays where it can be watched rather than on the very bottom edge.
 *     It also sets where the reveal RE-ARMS, since that is the same box.
 *
 * The 1200 ms timeout is a safety net for environments where an
 * IntersectionObserver exists but never delivers (Claude Code's preview pane
 * does exactly this). It fires ONLY if nothing has been delivered by then —
 * an unconditional reveal would play the animation while the section is still
 * thousands of pixels below the fold, and everything would already be in place
 * by the time the reader arrives.
 */
(function () {
  function setup(element) {
    if (element.dataset.revealInit) return;
    element.dataset.revealInit = '1';

    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (still.matches || !('IntersectionObserver' in window)) return;

    element.classList.add('is-anim');

    // An IntersectionObserver delivers a first entry as soon as it starts
    // observing, whether or not the target is visible — so receiving anything
    // at all is proof it works, and says nothing about where we are yet.
    var delivered = false;

    setTimeout(function () {
      if (!delivered) element.classList.add('is-in');
    }, 1200);

    var threshold = parseFloat(element.dataset.revealThreshold) || 0.35;
    var margin = element.dataset.revealMargin || '0px 0px -14% 0px';

    var observer = new IntersectionObserver(
      function (entries) {
        delivered = true;
        entries.forEach(function (entry) {
          // Two thresholds rather than a test on isIntersecting: that one is
          // true as soon as a single pixel shows, so it would never go back to
          // false at the right moment. Re-arming at 0 — not at `threshold` —
          // means the reset happens once the element is fully out of the
          // observed box, never under the reader's eyes.
          //
          // The observer is deliberately never disconnected: leaving and
          // coming back — scrolling up past it, then down again — has to
          // replay the entrance, which is only possible while it is still
          // being watched.
          if (entry.intersectionRatio >= threshold) {
            element.classList.add('is-in');
          } else if (entry.intersectionRatio <= 0) {
            element.classList.remove('is-in');
          }
        });
      },
      { threshold: [0, threshold], rootMargin: margin }
    );

    observer.observe(element);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-reveal]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor swaps a section's markup without reloading.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
